import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
  RateReview as ReviewIcon,
  Assignment as AssignmentIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { hasPermission } from '../../../utils/permissions';
import { useNotification } from '../../../components/Notification';
import { formatDateWithCulture } from '../../../utils/dateUtils';
import { LICENSE_STATUS_WAITING } from '../../../constants';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import MultiSelect from '../../../components/MultiSelect';
import DateRangePicker from '../../../components/DateRangePicker';
import AddLicenseModal from './AddLicenseModal';
import ReviewLicenseModal from './ReviewLicenseModal';
import AssignUnitModal from './AssignUnitModal';
import LicenseHistoryModal from './LicenseHistoryModal';
import {
  fetchInit,
  fetchLicenses,
  fetchLicenseById,
  selectLicenses,
  selectLicenseStatuses,
  selectLicenseTypes,
  selectAvailableUnits,
  selectInitLicense,
  selectCanReview,
  selectLicensesLoading,
  selectLicensesTotal,
  selectLicensesPage,
  selectLicensesPageSize,
  selectLicensesSortColumn,
  selectLicensesSortDirection,
  selectLicensesFilterCode,
  selectLicensesFilterStatuses,
  selectLicensesFilterLicenseTypes,
  selectLicensesFilterUnits,
  selectLicensesFilterExpiryDateFrom,
  selectLicensesFilterExpiryDateTo,
  selectLicensesError,
  selectLicensesSuccessMessage,
  selectSelectedLicense,
  selectUnits,
  selectUnitsTotal,
  selectUnitsPage,
  selectUnitsPageSize,
  selectUnitsLoading,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterCode,
  setFilterStatuses,
  setFilterLicenseTypes,
  setFilterUnits,
  setFilterExpiryDateFrom,
  setFilterExpiryDateTo,
  addLicense,
  updateLicense,
  deleteLicense,
  bulkDeleteLicenses,
  approveLicense,
  rejectLicense,
  extendLicense,
  revokeLicense,
  fetchUnits,
  assignUnit,
  setUnitsPage,
  setUnitsPageSize,
  importLicenses,
  exportLicenses,
  downloadTemplate,
  clearError,
  clearSuccessMessage,
  clearSelectedLicense,
} from './slice';

function Licenses() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const licenses = useSelector(selectLicenses);
  const licenseStatuses = useSelector(selectLicenseStatuses);
  const licenseTypes = useSelector(selectLicenseTypes);
  const availableUnits = useSelector(selectAvailableUnits);
  const initLicense = useSelector(selectInitLicense);
  const canReview = useSelector(selectCanReview);
  const loading = useSelector(selectLicensesLoading);
  const total = useSelector(selectLicensesTotal);
  const page = useSelector(selectLicensesPage);
  const pageSize = useSelector(selectLicensesPageSize);
  const sortColumn = useSelector(selectLicensesSortColumn);
  const sortDirection = useSelector(selectLicensesSortDirection);
  const filterCode = useSelector(selectLicensesFilterCode);
  const filterStatuses = useSelector(selectLicensesFilterStatuses);
  const filterLicenseTypes = useSelector(selectLicensesFilterLicenseTypes);
  const filterUnits = useSelector(selectLicensesFilterUnits);
  const filterExpiryDateFrom = useSelector(selectLicensesFilterExpiryDateFrom);
  const filterExpiryDateTo = useSelector(selectLicensesFilterExpiryDateTo);
  const error = useSelector(selectLicensesError);
  const successMessage = useSelector(selectLicensesSuccessMessage);
  const selectedLicense = useSelector(selectSelectedLicense);
  const units = useSelector(selectUnits);
  const unitsTotal = useSelector(selectUnitsTotal);
  const unitsPage = useSelector(selectUnitsPage);
  const unitsPageSize = useSelector(selectUnitsPageSize);
  const unitsLoading = useSelector(selectUnitsLoading);

  const [selectedIds, setSelectedIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [licenseToReview, setLicenseToReview] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [licenseToAssign, setLicenseToAssign] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [licenseForHistory, setLicenseForHistory] = useState(null);

  // Check permissions
  const canAddLicense = hasPermission('license', 'addLicense');
  const canEditLicense = hasPermission('license', 'editLicense');
  const canDeleteLicense = hasPermission('license', 'deleteLicense');

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Fetch licenses on mount and when filters change
  useEffect(() => {
    dispatch(fetchLicenses({ page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterLicenseTypes, filterUnits, filterExpiryDateFrom, filterExpiryDateTo }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterLicenseTypes, filterUnits, filterExpiryDateFrom, filterExpiryDateTo]);

  // Show notifications
  useEffect(() => {
    if (error) {
      showNotification(t(error) || error, 'error');
      dispatch(clearError());
    }
  }, [error, t, showNotification, dispatch]);

  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      // Refresh table after successful operation
      dispatch(fetchLicenses({ page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterLicenseTypes, filterUnits, filterExpiryDateFrom, filterExpiryDateTo }));
      setIsModalOpen(false);
      setIsReviewModalOpen(false);
      setIsAssignModalOpen(false);
      setSelectedIds([]);
    }
  }, [successMessage, showNotification, dispatch, page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterLicenseTypes, filterUnits, filterExpiryDateFrom, filterExpiryDateTo]);

  const handleAddLicense = () => {
    // Clear previous selected license
    dispatch(clearSelectedLicense());
    // Fetch fresh init data (new code and key) before opening modal
    dispatch(fetchInit());
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (row) => {
    // Fetch license details by ID
    dispatch(fetchLicenseById(row.id));
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleViewHistory = (row) => {
    setLicenseForHistory(row);
    setIsHistoryModalOpen(true);
  };

  const handleDelete = (row) => {
    setLicenseToDelete(row);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (licenseToDelete?.id) {
      dispatch(deleteLicense(licenseToDelete.id));
    }
    setShowDeleteDialog(false);
    setLicenseToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setLicenseToDelete(null);
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteLicenses(selectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  const handleModalSubmit = (formData) => {
    if (editMode) {
      dispatch(updateLicense(formData));
    } else {
      dispatch(addLicense(formData));
    }
  };

  const handleReview = (row) => {
    setLicenseToReview(row);
    setIsReviewModalOpen(true);
  };

  const handleApprove = (id) => {
    dispatch(approveLicense(id));
  };

  const handleReject = (id, statusNote) => {
    dispatch(rejectLicense({ id, statusNote }));
  };

  const handleExtend = (id, newExpiryDate) => {
    dispatch(extendLicense({ id, newExpiryDate }));
  };

  const handleRevoke = (id, statusNote) => {
    dispatch(revokeLicense({ id, statusNote }));
  };

  const handleAssignToUnit = (row) => {
    setLicenseToAssign(row);
    setIsAssignModalOpen(true);
    // Fetch units when opening the modal
    dispatch(fetchUnits({ page: 1, pageSize: 10 }));
  };

  const handleAssignUnit = (unitId) => {
    if (licenseToAssign?.id) {
      dispatch(assignUnit({ id: licenseToAssign.id, unitId }));
    }
  };

  const handleImportClick = () => {
    setShowImportDialog(true);
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files[0];
    setImportFile(file);
  };

  const handleImportConfirm = () => {
    if (importFile) {
      dispatch(importLicenses(importFile));
      setShowImportDialog(false);
      setImportFile(null);
    }
  };

  const handleImportCancel = () => {
    setShowImportDialog(false);
    setImportFile(null);
  };

  const handleExportClick = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportExcel = () => {
    const filters = {
      code: filterCode,
      status: filterStatuses,
      licenseType: filterLicenseTypes,
      unitId: filterUnits,
    };
    dispatch(exportLicenses({ exportType: 'excel', filters }));
    handleExportClose();
  };

  const handleExportPdf = () => {
    const filters = {
      code: filterCode,
      status: filterStatuses,
      licenseType: filterLicenseTypes,
      unitId: filterUnits,
    };
    dispatch(exportLicenses({ exportType: 'pdf', filters }));
    handleExportClose();
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadTemplate());
  };

  const columns = [
    { key: 'code', label: t('licenses.table.code') },
    { key: 'key', label: t('licenses.table.key') },
    { key: 'unitName', label: t('licenses.table.unitName') },
    { key: 'description', label: t('licenses.table.description') },
    {
      key: 'issueDate',
      label: t('licenses.table.issueDate'),
      render: (value) => formatDateWithCulture(value, i18n.language) || '-',
    },
    {
      key: 'expiryDate',
      label: t('licenses.table.expiryDate'),
      render: (value) => formatDateWithCulture(value, i18n.language) || '-',
    },
    {
      key: 'status',
      label: t('licenses.table.status'),
      width: '140px',
      render: (value) => {
        const statusObj = licenseStatuses.find((status) => status.id === value);
        const statusName = statusObj ? statusObj.name : '-';
        return (
          <Tooltip title={statusName} arrow placement="top">
            <Box
              component="span"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: statusObj ? '#e3f2fd' : '#e2e8f0',
                color: statusObj ? '#1565c0' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              {statusName}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'licenseType',
      label: t('licenses.table.licenseType'),
      width: '180px',
      render: (value) => {
        const typeObj = licenseTypes.find((type) => type.id === value);
        const typeName = typeObj ? typeObj.name : '-';
        return (
          <Tooltip title={typeName} arrow placement="top">
            <Box
              component="span"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: typeObj ? '#f3e5f5' : '#e2e8f0',
                color: typeObj ? '#7b1fa2' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              {typeName}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'maxUsers',
      label: t('licenses.table.maxUsers'),
      width: '200px',
      render: (value, row) => {
        const currentUsers = row.currentUsers || 0;
        const maxUsers = value || 0;
        const isExceeded = currentUsers > maxUsers;
        const tooltipText = isExceeded ? t('licenses.userLimitExceeded') : `${currentUsers}/${maxUsers}`;
        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <span
              style={{
                color: isExceeded ? '#c62828' : 'inherit',
                fontWeight: isExceeded ? 600 : 'normal',
              }}
            >
              {currentUsers}/{maxUsers}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'version',
      label: t('licenses.table.version'),
      width: '150px',
      render: (value) => value || '-',
    },
    {
      key: 'maxDevices',
      label: t('licenses.table.maxDevices'),
      width: '200px',
      render: (value, row) => {
        const approvedCount = row.approvedDeviceCount || 0;
        const isExceeded = approvedCount > value;
        const tooltipText = isExceeded ? t('licenses.deviceLimitExceeded') : `${approvedCount}/${value}`;
        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <span
              style={{
                color: isExceeded ? '#c62828' : 'inherit',
                fontWeight: isExceeded ? 600 : 'normal',
              }}
            >
              {approvedCount}/{value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'maxGroupDevice',
      label: t('licenses.table.maxGroupDevice'),
      width: '200px',
      render: (value, row) => {
        const currentGroupDevices = row.currentGroupDevices || 0;
        const maxGroupDevice = value || 0;
        const isExceeded = currentGroupDevices > maxGroupDevice;
        const tooltipText = isExceeded ? t('licenses.groupDeviceLimitExceeded') : `${currentGroupDevices}/${maxGroupDevice}`;
        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <span
              style={{
                color: isExceeded ? '#c62828' : 'inherit',
                fontWeight: isExceeded ? 600 : 'normal',
              }}
            >
              {currentGroupDevices}/{maxGroupDevice}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'launchDate',
      label: t('licenses.table.launchDate'),
      render: (value) => formatDateWithCulture(value, i18n.language) || '-',
    },
    {
      key: 'supportedFeatures',
      label: t('licenses.table.supportedFeatures'),
      width: '200px',
      render: (value) => value || '-',
    },
  ];

  // Custom actions renderer to include Review and Assign buttons
  const renderActions = (row) => (
    <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', justifyContent: 'flex-start' }}>
      {canReview && (
        <IconButton
          onClick={() => handleAssignToUnit(row)}
          title={t('licenses.actions.assignToUnit')}
          color="success"
          size="small"
          disabled={row.status !== LICENSE_STATUS_WAITING}
          id={`licenses-table-button-assign-${row.id}`}
          name={`licenses-table-button-assign-${row.id}`}
          data-testid={`licenses-table-button-assign-${row.id}`}
          sx={{ p: 0.5 }}
        >
          <AssignmentIcon fontSize="small" />
        </IconButton>
      )}
      {canReview && (
        <IconButton
          onClick={() => handleReview(row)}
          title={t('licenses.actions.review')}
          color="info"
          size="small"
          disabled={row.unitId == null}
          id={`licenses-table-button-review-${row.id}`}
          name={`licenses-table-button-review-${row.id}`}
          data-testid={`licenses-table-button-review-${row.id}`}
          sx={{ p: 0.5 }}
        >
          <ReviewIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        onClick={() => handleViewHistory(row)}
        title={t('licenses.actions.viewHistory')}
        color="secondary"
        size="small"
        id={`licenses-table-button-history-${row.id}`}
        name={`licenses-table-button-history-${row.id}`}
        data-testid={`licenses-table-button-history-${row.id}`}
        sx={{ p: 0.5 }}
      >
        <HistoryIcon fontSize="small" />
      </IconButton>
      {canEditLicense && (
        <IconButton
          onClick={() => handleEdit(row)}
          title={t('licenses.actions.edit')}
          color="primary"
          size="small"
          id={`licenses-table-button-edit-${row.id}`}
          name={`licenses-table-button-edit-${row.id}`}
          data-testid={`licenses-table-button-edit-${row.id}`}
          sx={{ p: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDeleteLicense && (
        <IconButton
          onClick={() => handleDelete(row)}
          title={t('licenses.actions.delete')}
          color="error"
          size="small"
          id={`licenses-table-button-delete-${row.id}`}
          name={`licenses-table-button-delete-${row.id}`}
          data-testid={`licenses-table-button-delete-${row.id}`}
          sx={{ p: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DescriptionIcon fontSize="small" />
            <Typography variant="body2">{t('licenses.breadcrumb.licenseManagement')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('licenses.breadcrumb.licenses')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('licenses.title')}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {canAddLicense && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddLicense}
                id="addLicense"
                name="addLicense"
                data-testid="licenses-button-addLicense"
              >
                {t('licenses.addLicense')}
              </Button>
            )}
            {canAddLicense && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<UploadIcon />}
                onClick={handleImportClick}
                id="importLicenses"
                name="importLicenses"
                data-testid="licenses-button-importLicenses"
              >
                {t('licenses.import')}
              </Button>
            )}
            <Button
              variant="outlined"
              color="info"
              startIcon={<DownloadIcon />}
              onClick={handleExportClick}
              id="exportLicenses"
              name="exportLicenses"
              data-testid="licenses-button-exportLicenses"
            >
              {t('licenses.export')}
            </Button>
            {canDeleteLicense && selectedIds.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="licenses-button-deleteSelected"
              >
                {t('licenses.deleteSelected')} ({selectedIds.length})
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Input
              label={t('licenses.filterByCode')}
              value={filterCode}
              onChange={(e) => dispatch(setFilterCode(e.target.value))}
              placeholder={t('licenses.filterByCodePlaceholder')}
              id="filterCode"
              name="filterCode"
              data-testid="licenses-input-filterCode"
              sx={{ width: 320 }}
            />
            <MultiSelect
              label={t('licenses.filterByStatus')}
              value={filterStatuses}
              onChange={(e) => dispatch(setFilterStatuses(e.target.value))}
              options={licenseStatuses}
              placeholder={t('licenses.filterByStatusPlaceholder')}
              id="filterStatuses"
              name="filterStatuses"
              data-testid="licenses-select-filterStatuses"
              sx={{ width: 320 }}
            />
            <MultiSelect
              label={t('licenses.filterByLicenseType')}
              value={filterLicenseTypes}
              onChange={(e) => dispatch(setFilterLicenseTypes(e.target.value))}
              options={licenseTypes}
              placeholder={t('licenses.filterByLicenseTypePlaceholder')}
              id="filterLicenseTypes"
              name="filterLicenseTypes"
              data-testid="licenses-select-filterLicenseTypes"
              sx={{ width: 320 }}
            />
            <MultiSelect
              label={t('licenses.filterByUnit')}
              value={filterUnits}
              onChange={(e) => dispatch(setFilterUnits(e.target.value))}
              options={availableUnits}
              placeholder={t('licenses.filterByUnitPlaceholder')}
              id="filterUnits"
              name="filterUnits"
              data-testid="licenses-select-filterUnits"
              sx={{ width: 320 }}
            />
            <DateRangePicker
              label={t('licenses.filterByExpiryDateRange')}
              fromValue={filterExpiryDateFrom || ''}
              toValue={filterExpiryDateTo || ''}
              onFromChange={(e) => dispatch(setFilterExpiryDateFrom(e.target.value))}
              onToChange={(e) => dispatch(setFilterExpiryDateTo(e.target.value))}
              id="filterExpiryDateRange"
              name="filterExpiryDateRange"
              data-testid="licenses-daterangepicker-filterExpiryDate"
              sx={{ width: 320 }}
            />
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
          <Table
            id="licenses-table"
            columns={columns}
            data={licenses}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            renderActions={renderActions}
            onRowDoubleClick={canEditLicense ? handleEdit : null}
            loading={loading}
            emptyMessage={t('licenses.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={(newPage) => dispatch(setPage(newPage))}
            onPageSizeChange={(newSize) => dispatch(setPageSize(newSize))}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={(col, dir) => {
              dispatch(setSortColumn(col));
              dispatch(setSortDirection(dir));
            }}
            sortableColumns={['code', 'key', 'unitName', 'description', 'issueDate', 'expiryDate', 'status', 'licenseType', 'maxUsers', 'maxDevices', 'maxGroupDevice', 'launchDate']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('licenses.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('licenses.deleteDialog.message', {
              code: licenseToDelete?.code || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="licenses-button-cancelDelete"
          >
            {t('licenses.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="licenses-button-confirmDelete"
          >
            {t('licenses.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('licenses.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('licenses.bulkDeleteDialog.message', {
              count: selectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="licenses-button-cancelBulkDelete"
          >
            {t('licenses.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="licenses-button-confirmBulkDelete"
          >
            {t('licenses.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Modal */}
      <AddLicenseModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          dispatch(clearSelectedLicense());
        }}
        onSubmit={handleModalSubmit}
        editMode={editMode}
        licenseData={selectedLicense}
        initLicense={initLicense}
        licenseTypes={licenseTypes}
      />

      {/* Review Modal */}
      <ReviewLicenseModal
        open={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        licenseData={licenseToReview}
        licenseStatuses={licenseStatuses}
        onApprove={handleApprove}
        onReject={handleReject}
        onExtend={handleExtend}
        onRevoke={handleRevoke}
        i18n={i18n}
      />

      {/* Assign Unit Modal */}
      <AssignUnitModal
        open={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        units={units}
        unitsTotal={unitsTotal}
        unitsPage={unitsPage}
        unitsPageSize={unitsPageSize}
        unitsLoading={unitsLoading}
        onPageChange={(newPage) => dispatch(setUnitsPage(newPage))}
        onPageSizeChange={(newSize) => dispatch(setUnitsPageSize(newSize))}
        onAssign={handleAssignUnit}
      />

      {/* Import Dialog */}
      <Dialog
        open={showImportDialog}
        onClose={handleImportCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'success.main',
            pb: 1,
          }}
        >
          <UploadIcon />
          {t('licenses.importDialog.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ mb: 3 }}>
            {t('licenses.importDialog.message')}
          </DialogContentText>

          {/* Download Template Section */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px dashed #ccc',
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('licenses.importDialog.templateSection')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={handleDownloadTemplate}
              fullWidth
              id="downloadTemplateInDialog"
              name="downloadTemplateInDialog"
              data-testid="licenses-button-downloadTemplateInDialog"
            >
              {t('licenses.downloadTemplate')}
            </Button>
          </Box>

          {/* File Upload Section */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('licenses.importDialog.uploadSection')}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                py: 1.5,
                justifyContent: 'flex-start',
                color: importFile ? 'success.main' : 'text.secondary',
                borderColor: importFile ? 'success.main' : 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                  {importFile ? importFile.name : t('licenses.importDialog.selectFile')}
                </Typography>
              </Box>
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleImportFileChange}
                id="importFile"
                name="importFile"
                data-testid="licenses-input-importFile"
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={handleImportCancel}
            variant="outlined"
            id="cancelImport"
            name="cancelImport"
            data-testid="licenses-button-cancelImport"
          >
            {t('licenses.importDialog.cancel')}
          </Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            color="success"
            startIcon={<UploadIcon />}
            disabled={!importFile}
            id="confirmImport"
            name="confirmImport"
            data-testid="licenses-button-confirmImport"
          >
            {t('licenses.importDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
      >
        <MenuItem
          onClick={handleExportExcel}
          id="exportExcel"
          data-testid="licenses-menu-exportExcel"
        >
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('licenses.exportExcel')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleExportPdf}
          id="exportPdf"
          data-testid="licenses-menu-exportPdf"
        >
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('licenses.exportMenu.pdf')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* License History Modal */}
      <LicenseHistoryModal
        open={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        license={licenseForHistory}
      />
    </Box>
  );
}

export default Licenses;
